"use client";

import { useState, useRef, useEffect } from "react";
import {
  Camera,
  Upload,
  Loader2,
  Bot,
  Image as ImageIcon,
  Check,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import { useAssistantAvatar } from "../contexts/AssistantAvatarContext";
import { useAuth } from "@workos-inc/authkit-react";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import api from "../http";
import { appConfig } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";

const ASSISTANT_NAME = appConfig.assistantName;

interface AvailablePhoto {
  url: string;
  name: string;
  size: number;
  last_modified?: string | null;
}

export default function Settings() {
  const { toast } = useToast();
  const {
    assistantAvatarUrl,
    isLoading,
    uploadAssistantPhoto,
    setAssistantPhoto,
    isLoadingPhoto,
  } = useAssistantAvatar();
  const { getAccessToken, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Available photos state
  const [availablePhotos, setAvailablePhotos] = useState<AvailablePhoto[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Fehler beim Laden des aktuellen Benutzers:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Load available photos from storage
  useEffect(() => {
    const loadAvailablePhotos = async () => {
      setIsLoadingPhotos(true);
      try {
        const token = await getAccessToken();
        const response = await api.getAvailableAssistantPhotos(token);
        setAvailablePhotos(response.available_photos);
      } catch (error) {
        console.error("Fehler beim Laden der verfügbaren Fotos:", error);
        setAvailablePhotos([]);
      } finally {
        setIsLoadingPhotos(false);
      }
    };

    if (user) {
      loadAvailablePhotos();
    }
  }, [user, getAccessToken]);

  const reloadAvailablePhotos = async () => {
    setIsLoadingPhotos(true);
    try {
      const token = await getAccessToken();
      const response = await api.getAvailableAssistantPhotos(token);
      setAvailablePhotos(response.available_photos);
    } catch (error) {
      console.error("Fehler beim Neuladen der verfügbaren Fotos:", error);
      setAvailablePhotos([]);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie eine Bilddatei aus (JPEG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      toast({
        title: "Datei zu groß",
        description: "Bitte wählen Sie ein Bild kleiner als 5MB aus",
        variant: "destructive",
      });
      return;
    }

    try {
      await uploadAssistantPhoto(file);
      toast({
        title: "Erfolgreich",
        description: "Assistenten-Foto erfolgreich aktualisiert",
      });
      await reloadAvailablePhotos(); // Reload photos after successful upload
    } catch (error) {
      toast({
        title: "Upload fehlgeschlagen",
        description:
          "Fehler beim Hochladen des Assistenten-Fotos. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelect = async (photoUrl: string) => {
    try {
      await setAssistantPhoto(photoUrl);
      toast({
        title: "Erfolgreich",
        description: "Assistenten-Foto erfolgreich aktualisiert",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description:
          "Fehler beim Auswählen des Fotos. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col h-full min-h-screen">
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-gray-200">
        <h1 className="text-xl font-semibold">Assistent Einstellungen</h1>
        <div className="flex items-center gap-2">
          <SettingsDropdown
            onOpenProfileModal={() => setIsProfileModalOpen(true)}
            currentUser={currentUser}
          />
        </div>
      </header>

      {/* Main Content Scrollable */}
      <div className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 max-w-4xl">
          <div className="grid gap-6">
            {/* Assistant Photo Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Bot className="h-5 w-5" />
                  <span>Assistenten-Foto</span>
                </CardTitle>
                <CardDescription>
                  Laden Sie ein neues Foto für {ASSISTANT_NAME} hoch, um Ihre
                  Erfahrung zu personalisieren
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Assistant Photo */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <Avatar className="h-32 w-32">
                      {isLoadingPhoto ? (
                        <AvatarFallback className="text-2xl animate-pulse">
                          <Bot className="h-8 w-8 text-gray-400" />
                        </AvatarFallback>
                      ) : (
                        <>
                          <AvatarImage
                            src={assistantAvatarUrl}
                            alt={`${ASSISTANT_NAME} Assistent`}
                            className="object-cover"
                          />
                          <AvatarFallback className="text-2xl">
                            <Bot className="h-8 w-8" />
                          </AvatarFallback>
                        </>
                      )}
                    </Avatar>
                    <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1">
                      <div className="h-3 w-3 bg-white rounded-full"></div>
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-lg font-semibold">{ASSISTANT_NAME}</h3>
                    <p className="text-sm text-muted-foreground">
                      Ihr KI-Assistent
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  {/* Upload Section */}
                  <div className="space-y-4">
                    <div className="text-center">
                      <h4 className="font-medium mb-2">Neues Foto hochladen</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Wählen Sie ein neues Bild für Ihren Assistenten.
                        Empfohlene Größe: 400x400 Pixel
                      </p>
                    </div>

                    {/* Drag & Drop Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        dragActive
                          ? "border-primary bg-primary/5"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center space-y-4">
                        <div className="p-3 bg-primary/10 rounded-full">
                          <Camera className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Ziehen Sie ein Bild hierher oder{" "}
                            <button
                              type="button"
                              onClick={handleUploadClick}
                              className="hover:underline font-medium px-2 py-1 rounded"
                              style={{ 
                                backgroundColor: `${APP_COLOR}20`,
                                color: APP_COLOR
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = APP_COLOR;
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = `${APP_COLOR}20`;
                                e.currentTarget.style.color = APP_COLOR;
                              }}
                            >
                              klicken Sie zum Durchsuchen
                            </button>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Unterstützt: JPG, PNG, GIF (max. 5MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Upload Button */}
                    <div className="flex justify-center">
                                             <Button
                         onClick={handleUploadClick}
                         disabled={isLoading}
                         className="flex items-center space-x-2 text-white transition-colors"
                         style={{
                           backgroundColor: APP_COLOR,
                           borderColor: APP_COLOR
                         }}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.backgroundColor = APP_COLOR;
                           e.currentTarget.style.filter = 'brightness(0.8)';
                           e.currentTarget.style.color = 'white';
                           e.currentTarget.style.borderColor = '#3b82f6';
                           e.currentTarget.style.borderWidth = '1px';
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.backgroundColor = APP_COLOR;
                           e.currentTarget.style.filter = 'none';
                           e.currentTarget.style.color = 'white';
                           e.currentTarget.style.borderColor = APP_COLOR;
                           e.currentTarget.style.borderWidth = '1px';
                         }}
                       >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        <span>
                          {isLoading
                            ? "Wird hochgeladen..."
                            : "Datei auswählen"}
                        </span>
                      </Button>
                    </div>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Available Photos Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ImageIcon className="h-5 w-5" />
                  <span>Verfügbare Fotos</span>
                </CardTitle>
                <CardDescription>
                  Wählen Sie aus zuvor hochgeladenen Assistenten-Fotos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingPhotos ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Lade verfügbare Fotos...
                    </span>
                  </div>
                ) : availablePhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {availablePhotos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative group cursor-pointer"
                        onClick={() => handlePhotoSelect(photo.url)}
                      >
                        <div className="relative">
                          <Avatar className="h-20 w-20 mx-auto">
                            <AvatarImage
                              src={photo.url}
                              alt={photo.name}
                              className="object-cover"
                            />
                            <AvatarFallback>
                              <Bot className="h-8 w-8" />
                            </AvatarFallback>
                          </Avatar>

                          {/* Selection indicator */}
                          {assistantAvatarUrl === photo.url && (
                            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}

                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-full transition-all duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <Button
                                size="sm"
                                variant="secondary"
                                className="text-xs"
                              >
                                Auswählen
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      Keine zuvor hochgeladenen Fotos verfügbar
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Laden Sie ein neues Foto hoch, um es hier zu sehen
                    </p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Hinweis
                      </p>
                      <p className="text-sm text-blue-700">
                        Klicken Sie auf ein Foto, um es als aktuelles
                        Assistenten-Foto zu verwenden. Das ausgewählte Foto wird
                        für alle Mitglieder Ihrer Organisation sichtbar sein.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Profile Image Modal */}
      {isProfileModalOpen && (
        <ProfileImageModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onProfileImageUpdate={handleProfileImageUpdate}
          onUserInfoUpdate={handleUserInfoUpdate}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}